import { Button, Label, Modal, TextInput, Select, Textarea, Carousel } from 'flowbite-react';
import { useState } from 'react';
import usePortofolioController from '../controllers/usePortofolioController';
import { useSession } from '../context/SessionContext';

function Portofolio() {
  const { session } = useSession();
  const [openModal, setOpenModal] = useState(false);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentPortofolio, setCurrentPortofolio] = useState(null);
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    category_id: '',
    description: '',
    url: '',
    type: '',
    achievements: ''
  });
  const [newCategory, setNewCategory] = useState('');

  const {
    portofolios,
    categories,
    loading,
    error,
    addPortofolio,
    updatePortofolio,
    deletePortofolio,
    addCategory,
    deleteImage
  } = usePortofolioController(session);

  const onCloseModal = () => {
    setOpenModal(false);
    setIsEditMode(false);
    setCurrentPortofolio(null);
    setImages([]);
    setImageError('');
    setFormData({
      title: '',
      category_id: '',
      description: '',
      url: '',
      type: '',
      achievements: ''
    });
  };

  const onCloseCategoryModal = () => {
    setOpenCategoryModal(false);
    setNewCategory('');
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleCategoryChange = (e) => {
    setNewCategory(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.category_id) {
      alert('Please fill in all required fields: Title and Category.');
      return;
    }

    if (images.length > 0 && imageError) {
      alert('Please fix image errors before submitting');
      return;
    }

    try {
      if (isEditMode && currentPortofolio) {
        const newImageFiles = images.filter(img => img instanceof File);
        await updatePortofolio(currentPortofolio.id, formData, newImageFiles);
      } else {
        await addPortofolio(formData, images);
      }

      onCloseModal();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();

    if (!newCategory) {
      alert('Please enter a category name.');
      return;
    }

    try {
      await addCategory({ name: newCategory });
      onCloseCategoryModal();
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleEdit = (portofolio) => {
    setCurrentPortofolio(portofolio);
    setIsEditMode(true);
    setFormData({
      title: portofolio.title,
      category_id: portofolio.category_id,
      description: portofolio.description || '',
      url: portofolio.url || '',
      type: portofolio.type || '',
      achievements: portofolio.achievements || ''
    });
    setImages(portofolio.images || []);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this portofolio?')) {
      await deletePortofolio(id);
    }
  };

  const handleImageChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      const validFiles = [];
      for (const file of files) {
        if (file.size > 500 * 1024) {
          setImageError('Each image must be less than 500KB');
          return;
        }
        validFiles.push(file);
      }
      setImages((prevImages) => [...prevImages, ...validFiles]);
      setImageError('');
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (image, index) => {
    try {
      if (isEditMode && image.path) {
        await deleteImage(image.path);
        setCurrentPortofolio(prev => ({
          ...prev,
          images: prev.images.filter(img => img.path !== image.path)
        }));
      }
      setImages(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  return (
    <div className='flex'>
      <section className="flex-1 py-5 md:px-5 bg-gray-100 dark:bg-gray-900">
        <div className="grid md:grid-cols-3 grid-cols-2">
          <div className='my-6 md:col-span-2 md:pl-6'>
            <h1 className="text-3xl font-extrabold text-left lg:text-5xl 2xl:text-8xl">
              <span className="text-transparent bg-gradient-to-br bg-clip-text from-sky-800 via-sky-600 to-sky-700">
                Portofolio
              </span>
            </h1>
            <p className="max-w-3xl mx-auto mt-6 md:text-md text-sm mr-4 text-left text-gray-700 dark:text-white md:text-md">
              Showcase your projects and works to the world. Add your portofolio here.
            </p>
          </div>
          <div className="mx-auto my-auto col-span-1">
            <Button color="blue" onClick={() => setOpenModal(true)}>
              <b>+</b> Add Your Portofolio
            </Button>
          </div>
        </div>

        <div className="mt-6 space-y-8 px-4">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800">
              Error: {error}
            </div>
          ) : (
            portofolios.map((porto) => {
              const categoryName = categories.find(c => c.id === porto.category_id)?.name || 'Uncategorized';
              
              return (
                <div key={porto.id} className="relative group">
                  {/* Portfolio card */}
                  <div className="bg-white rounded-lg shadow-lg dark:bg-gray-800 md:p-6 p-3 transition-all duration-300 group-hover:shadow-xl group-hover:translate-x-2">
                    <div className="flex flex-col md:flex-row">
                      {porto.images?.length > 0 && (
                        <div className="md:w-2/5 lg:w-1/3 h-64 md:h-auto mb-4 md:mb-0">
                          <Carousel
                            indicators={false}
                            leftControl={<span className="text-white">❮</span>}
                            rightControl={<span className="text-white">❯</span>}
                          >
                            {porto.images.map((image, index) => (
                              <img
                                key={index}
                                src={image.publicUrl}
                                alt={`Portfolio ${index + 1}`}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ))}
                          </Carousel>
                        </div>
                      )}
                      
                      <div className={`flex-1 ${porto.images ? 'md:ml-6' : ''}`}>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                          <h2 className="md:text-xl font-bold text-gray-800 dark:text-white">
                            {porto.title}
                          </h2>
                          <span className="text-sm text-gray-500 dark:text-gray-400 mt-1 md:mt-0">
                            {categoryName} • {porto.type}
                          </span>
                        </div>

                        <div className="flex items-center mb-4 text-gray-600 dark:text-gray-300">
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                          {porto.url ? (
                            <a
                              href={porto.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline dark:text-blue-400"
                            >
                              {porto.url}
                            </a>
                          ) : (
                            <span>No URL provided</span>
                          )}
                        </div>

                        {porto.description && (
                          <p className="mb-4 text-gray-700 dark:text-gray-300">
                            {porto.description}
                          </p>
                        )}

                        {porto.achievements && (
                          <div className="bg-gray-50 dark:bg-gray-700 md:p-4 p-2 rounded-lg mb-4">
                            <h3 className="text-sm font-semibold mb-2 text-gray-800 dark:text-white">
                              Key Achievements:
                            </h3>
                            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                              {porto.achievements.split(";").map((item, idx) => (
                                <li key={idx}>{item.trim()}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button 
                            color="red" 
                            onClick={(e) => { e.stopPropagation(); handleDelete(porto.id); }}
                            size="sm"
                            className="flex-1 md:flex-none"
                          >
                            Delete
                          </Button>
                          <Button 
                            color="blue" 
                            onClick={(e) => { e.stopPropagation(); handleEdit(porto); }}
                            size="sm"
                            className="flex-1 md:flex-none"
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Modal for Adding/Editing Portofolio */}
        <Modal show={openModal} onClose={onCloseModal} popup className="backdrop-blur-sm bg-white/30">
          <Modal.Header />
          <Modal.Body>
            <form onSubmit={handleSubmit} className="space-y-6 p-5">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                {isEditMode ? 'Edit Portofolio' : 'Add Your Portofolio'}
              </h3>
              <div>
                <Label htmlFor="title" value="Title *" />
                <TextInput id="title" placeholder="e.g. Company Website" required value={formData.title} onChange={handleInputChange} />
              </div>
              <div>
                <Label htmlFor="category_id" value="Category *" />
                <div className="flex gap-2">
                  <Select id="category_id" required value={formData.category_id} onChange={handleInputChange}>
                    <option>Choose Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                  <Button color="blue" onClick={() => setOpenCategoryModal(true)}>
                    <b>+</b> Add Category
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="type" value="Type *" />
                <Select 
                  id="type" 
                  required 
                  value={formData.type} 
                  onChange={handleInputChange}
                >
                  <option value="">Choose Type</option>
                  <option value="freelance">Freelance</option>
                  <option value="personal project">Personal Project</option>
                  <option value="college project">College Project</option>
                  <option value="final project">Final Project</option>
                  <option value="thesis project">Thesis Project</option>
                  <option value="community service">Community Service</option>
                  <option value="research project">Research Project</option>
                  <option value="open source contribution">Open Source Contribution</option>
                </Select>
              </div>

              <div>
                <Label htmlFor="achievements" value="Key Achievements (separate with ;)" />
                <Textarea
                  id="achievements"
                  placeholder="Achievements 1; Achievements 2; ..."
                  value={formData.achievements}
                  onChange={handleInputChange}
                />
              </div>
            
              <div>
                <Label htmlFor="description" value="Description" />
                <Textarea id="description" placeholder="Describe your portofolio" value={formData.description} onChange={handleInputChange} />
              </div>
              <div>
                <Label htmlFor="url" value="Link / URL" />
                <TextInput id="url" placeholder="https://myweb.com" value={formData.url} onChange={handleInputChange} />
              </div>
              <div>
                <Label htmlFor="images" value="Image Documentation (Max 500KB per image)" />
                <input
                  id="images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                />
                {imageError && <p className="mt-2 text-sm text-red-600 dark:text-red-500">{imageError}</p>}
                <div className="mt-4 space-y-2">
                  {images?.map((image, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center">
                        {image.publicUrl && (
                          <img src={image.publicUrl} alt={`Preview ${index + 1}`} className="w-12 h-12 object-cover rounded mr-3" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                          {image.path ? image.path.split('/').pop() : image.name}
                        </span>
                      </div>
                      <Button 
                        color="red" 
                        size="xs" 
                        onClick={() => handleRemoveImage(image, index)}
                        className="ml-2"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full">
                <Button color="blue" type="submit" className="w-full">
                  {isEditMode ? 'Update Portofolio' : 'Add Portofolio'}
                </Button>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        {/* Modal for Adding Category */}
        <Modal show={openCategoryModal} onClose={onCloseCategoryModal} popup className="backdrop-blur-sm bg-white/30">
          <Modal.Header />
          <Modal.Body>
            <form onSubmit={handleAddCategory} className="space-y-6 p-5">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">Add New Category</h3>
              <div>
                <Label htmlFor="newCategory" value="Category Name *" />
                <TextInput id="newCategory" placeholder="e.g. Web Development" required value={newCategory} onChange={handleCategoryChange} />
              </div>
              <div className="w-full">
                <Button color="blue" type="submit">
                  Add Category
                </Button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      </section>
    </div>
  );
}

export default Portofolio;